import { EntityTypeEnum, ThanksPageType } from "@funded-here-interface/common/src/constant/enum";
import { getThanksPageUrl } from "@funded-here-interface/common/src/Utils/navigation/constants";
import { FC, ReactNode } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";

import { RootState } from "../../store";

interface InvestmentAccountGateProps {
  children: ReactNode;
}

/**
 * Which waiting page an un-activated user is held on.
 *
 * The institution authorised representative is the person who actually
 * executed the agreement, so they keep the acknowledgement of that action.
 * Everyone else in the org — and every individual investor, who reaches this
 * point through a different flow — gets the neutral setup-in-progress copy.
 */
const waitingPageFor = (entityType: EntityTypeEnum | null, isAuthRep: boolean | null): string =>
  entityType === EntityTypeEnum.INSTITUTION && isAuthRep
    ? getThanksPageUrl(ThanksPageType.AGREEMENT_ACCOUNT_SETUP)
    : getThanksPageUrl(ThanksPageType.INVESTMENT_ACCOUNT_PENDING);

/**
 * Blocks the investor portal until the backend reports the investment account
 * as active. Wrapping the layout (rather than only redirecting after login) is
 * what makes a deep link or a refresh honour the gate.
 *
 * Reads the verdict the profile call already put in Redux — no request of its
 * own, so there is no loading flash and no extra round-trip on navigation.
 * Redux is persisted to sessionStorage, so the value survives a refresh; if it
 * is stale (activation landed since login) the waiting page's poll corrects it
 * within one interval and dispatches the user back through.
 */
const InvestmentAccountGate: FC<InvestmentAccountGateProps> = ({ children }) => {
  const entityType = useSelector((state: RootState) => state.auth.entityType);
  const isAuthRep = useSelector((state: RootState) => state.auth.isAuthRep);
  const isActive = useSelector((state: RootState) => state.auth.investmentAccountIsActive);

  // `null` is "not applicable", not "pending": the backend omits the
  // investmentAccount block for merchants and admins, who have no investment
  // account. Gating on a falsy check here would lock them out of shared routes.
  if (isActive === false) {
    return <Navigate to={waitingPageFor(entityType, isAuthRep)} replace />;
  }

  return <>{children}</>;
};

export default InvestmentAccountGate;
